/**
 * Echo Hunt -- the Play tab (P9, §10 + §12).
 *
 * The first playable surface: a woven round is shown bullet by bullet, the player rates each one
 * "LLM-sourced" (0) -> "dream-sourced" (100), is told how many bullets are dream-sourced, and submits.
 * The reveal then shows the score, which marks were right, and each real bullet's original journal text.
 *
 * ## The one rule this file must not break (§16.0 "label hygiene")
 *
 * Everything above the reveal renders from `HuntRoundView`, which carries `{slotId, text}` per bullet plus
 * the COUNT of dream-sourced entries -- and no labels. The labelled `HuntRevealView` is only ever built by
 * `SubmitRound_Live`, after submission. So there is deliberately no code path here that could color, sort
 * or annotate a bullet from its provenance before the round is over: the type it holds has no such field.
 *
 * The one label-derived value the player IS given is `realCount` (§10.1 tells them "X"), and that is
 * public information in the game's own rules: knowing HOW MANY are real is the game, knowing WHICH is the
 * answer.
 *
 * ## Progress across a reload (§16.2 P9 acceptance)
 *
 * The draft sheet is persisted (`SaveAnswers_Live`) on a debounce while playing, and re-seeded from the
 * stored `answers` on load. That is why the reveal needs a `revealedAt` stamp to tell "in progress" from
 * "already revealed" -- a round with answers is not necessarily a finished one.
 */
import React, {useEffect, useRef, useState} from "react";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import {Button, Column, Row, Select, Text, TextArea} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, InfoButton, RunInAction_Set} from "web-vcore";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {store} from "../../../Store/index.js";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {
	DEFAULT_ROUND_BULLET_COUNT,
	GenerateRound_Live,
	GetMyEchoRounds,
	GetPlayableRoundView_Live,
	GetPlayableRound_Live,
	GetPlayableStoryRevealedRounds_Live,
	GetRevealView_Live,
	GetStoredAnswersForRound_Live,
	JudgeExistingRound_Live,
	MarkRoundDiscarded_Live,
	RecordRevealOpen_Live,
	SaveAnswers_Live,
	SaveTellPhrase_Live,
	SubmitRound_Live,
} from "../../../EchoHunt/Round_Live.js";
import {GetHighlightedSlotIds, HuntRevealView, HuntRoundView, IsRoundFlaggedEasy, IsRoundStaleAgainstStory, IsRoundUnjudged, SlotText} from "../../../EchoHunt/Round.js";
import {DescribeJudgeLoopSummary} from "../../../EchoHunt/Judge.js";
import {JudgeInspectorPanel} from "./JudgeInspectorPanel.js";
import {DEFAULT_CONFIDENCE, HuntScore} from "../../../EchoHunt/Scoring.js";
import {RecallKind, RecallKind_values} from "../../../EchoHunt/Types.js";
import type {DoorVerdict} from "../../../EchoHunt/Types.js";
import {
	GetMyEchoStories_Live,
	GetStoryCharacter_Live,
	GetStoryRounds_Live,
	GetStoryWorld_Live,
	GetStoryWorldId_Live,
	SetDoorVerdict_Live,
} from "../../../EchoHunt/Story_Live.js";
import type {SuspicionApplicationResult} from "../../../EchoHunt/Story_Live.js";
import {CanAnswerDoor, DescribeDoorBlock} from "../../../EchoHunt/Story.js";
import {GetActiveStory_Live, GetRunningStories_Live, GetWorldPool_Live} from "../../../EchoHunt/Weave_Live.js";

/** Which story the round on this tab belongs to. Both states are silent without this line, and with several
 * stories alive the switcher is on the Story tab while its consequence is HERE. */
const ActiveStoryLine = observer_mgl(()=>{
	const story = GetActiveStory_Live();
	const running = GetRunningStories_Live();

	if (story == null) {
		return (
			<Row mt={6} center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, opacity: .6}}>
					No story selected -- building a round will start one.
				</Text>
			</Row>
		);
	}

	const character = GetStoryCharacter_Live(story);
	const world = GetStoryWorld_Live(story);

	return (
		<Row mt={6} center style={{gap: 8, flexWrap: "wrap"}}>
			<Text style={{fontSize: 11, opacity: .6}}>
				{`Continuing: ${character?.name ?? "(no character)"} in ${world?.name ?? "?"}`}
			</Text>
			{running.length > 1 &&
				<Text style={{fontSize: 11, opacity: .45}}>
					{`(${running.length} stories running -- switch on the Story tab)`}
				</Text>}
		</Row>
	);
});

// the panel
// ==========
export const PlayPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const roundView = GetPlayableRoundView_Live();

	const [generating, setGenerating] = useState(false);
	const [error, setError] = useState<string|null>(null);
	const [reveal, setReveal] = useState<HuntRevealView|null>(null);
	/** P11: what the meter did to the story when this round was submitted (§8.4). Kept beside the reveal
	 * because it is shown with it -- "the character noticed" is the closing beat of the round the player
	 * just finished, not a separate screen. */
	const [suspicion, setSuspicion] = useState<SuspicionApplicationResult|null>(null);

	const generate = async()=>{
		setGenerating(true);
		setError(null);
		setReveal(null);
		setSuspicion(null);
		try {
			const result = await GenerateRound_Live();
			if (!result.ok) setError(result.error);
		} catch (ex) {
			setError(`${ex}`);
		}
		setGenerating(false);
	};

	if (profile == null) {
		return <Text style={{opacity: .7, padding: 10}}>Create an Echo Hunt profile (Settings tab) to play.</Text>;
	}

	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 15}}>Echo Hunt</Text>
				<InfoButton text={[
					`A story round of about ${DEFAULT_ROUND_BULLET_COUNT} bullets. Some came from your own dreams;`,
					"some were written by an LLM and disguised to match. You are told HOW MANY are yours --",
					"finding WHICH is the exercise.",
					"",
					"Rate every bullet 0 (definitely LLM-sourced) to 100 (definitely dream-sourced). Your X",
					"highest-rated bullets are your guess and are outlined as you go. Missing an echo costs as much",
					"as a false alarm.",
					"",
					"The response being rehearsed is: 'this is oddly familiar -- it could be a dream'. Making the mark",
					"IS the practice, so make it quickly and don't audit yourself.",
				].join("\n")}/>
			</Row>

			{/* WHICH story this round belongs to (P11 follow-up). With several stories running at once, the Play
			    tab needs to say whose round it is showing -- otherwise switching on the Story tab silently changes
			    what this tab continues, and the only clue would be the prose itself. */}
			<ActiveStoryLine/>

			<Row mt={8} center style={{gap: 8, flexWrap: "wrap"}}>
				<Button
					text={generating ? "Building…" : (roundView != null ? "Discard and build a new round" : "Build a round")}
					enabled={!generating} onClick={generate}/>
				{generating && <ProgressSpinner/>}
				{roundView == null && !generating && reveal == null &&
					<Text style={{opacity: .6}}>No round in progress -- build one to play.</Text>}
			</Row>

			{error != null && <Text sel mt={8} style={{color: "red"}}>{error}</Text>}

			{roundView != null && reveal == null && !generating && <StaleRoundNotice roundId={roundView.roundId}/>}

			{roundView != null && reveal == null && !generating &&
				<JudgeStatus roundId={roundView.roundId} onError={setError}/>}

			{roundView != null && reveal == null && !generating &&
				<HuntView round={roundView} onSubmitted={(nextReveal, nextSuspicion)=>{
					setReveal(nextReveal);
					setSuspicion(nextSuspicion);
				}} onError={setError}/>}

			{reveal != null && <RevealView reveal={reveal} suspicion={suspicion} onClose={()=>{
				// the round is revealed now, so `GetPlayableRoundView_Live` stops returning it
				setReveal(null);
				setSuspicion(null);
			}}/>}

			{roundView == null && reveal == null && <RecentReveals onOpen={reveal=>{
				setReveal(reveal);
				setSuspicion(null); // a re-opened round's meter movement already happened; do not replay it
			}}/>}
		</ScrollView>
	);
});

// the hunt (§10.1)
// ==========

/** One slot's in-flight rating. A plain object so the debounced save can snapshot it cheaply. */
type DraftRating = {
	confidence: number;
	recallKind?: RecallKind|null;
	why?: string|null;
	msToMark: number;
};
type DraftSheet = Record<string, DraftRating>;

/** The one-line verdict on whether a stranger could have told this round apart, plus the "judge it now"
 * action. Renders from the ROUND (not the label-free view) because it is a property of the round, not a label. */
const JudgeStatus = observer_mgl((props: {roundId: string, onError: (message: string)=>void})=>{
	const {roundId, onError} = props;
	const round = GetPlayableRound_Live();
	const [judging, setJudging] = useState(false);

	if (round == null || round.id != roundId) return <></>;

	if (IsRoundUnjudged(round)) {
		return (
			<Row mt={8} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{color: "orange", fontSize: 12}}>
					This round has not been through the judge loop yet, so a stranger might be able to tell which bullets are yours.
				</Text>
				<Button text={judging ? "Judging…" : "Judge it now"} enabled={!judging} onClick={async()=>{
					setJudging(true);
					try {
						const result = await JudgeExistingRound_Live(roundId);
						if (!result.ok) onError(result.error);
					} catch (ex) {
						onError(`${ex}`);
					}
					setJudging(false);
				}}/>
				{judging && <ProgressSpinner/>}
			</Row>
		);
	}

	if (!IsRoundFlaggedEasy(round)) return <></>;

	return (
		<Row mt={8} center style={{gap: 8, flexWrap: "wrap"}}>
			<Text style={{color: "orange", fontSize: 12}}>
				{"Easy round (§9.4): the judge loop could not hide this one. "
					+ "A stranger could probably have told which bullets came from your dreams, so a high score here means less than usual."}
			</Text>
			<InfoButton text={DescribeJudgeLoopSummary(round.judgeSummary)}/>
		</Row>
	);
});

const HuntView = observer_mgl((props: {
	round: HuntRoundView,
	onSubmitted: (reveal: HuntRevealView, suspicion: SuspicionApplicationResult|null)=>void,
	onError: (message: string)=>void,
})=>{
	const {round, onSubmitted, onError} = props;

	const [drafts, setDrafts] = useState<DraftSheet>({});
	const [submitting, setSubmitting] = useState(false);
	const [saveState, setSaveState] = useState<"idle"|"saving"|"saved">("idle");

	/** when the round was first put on screen -- the origin for every `msToMark` (§10, §13.1) */
	const startedAt = useRef<number>(Date.now());
	/** the latest sheet, so the debounced save never captures a stale render */
	const latestDrafts = useRef<DraftSheet>({});
	latestDrafts.current = drafts;
	/** the round is rebuilt on every render (it is a derived view), so the effects below read it through a
	 * ref rather than listing a fresh object as a dependency */
	const latestRound = useRef<HuntRoundView>(round);
	latestRound.current = round;

	// seed from the stored answers (reload-restore, §16.2 P9). Keyed on the round, so a new round never
	// inherits the previous one's marks; the bullets come from the ref since they cannot change mid-round.
	const roundId = round.roundId;
	useEffect(()=>{
		startedAt.current = Date.now();
		const currentRound = latestRound.current;
		const stored = GetStoredAnswersForRound_Live(currentRound.roundId);
		const seeded: DraftSheet = {};
		for (const bullet of currentRound.bullets) {
			const answer = stored.find(a=>a.slotId == bullet.slotId) ?? null;
			seeded[bullet.slotId] = {
				confidence: answer?.confidence ?? DEFAULT_CONFIDENCE,
				recallKind: answer?.recallKind ?? null,
				why: answer?.why ?? null,
				msToMark: answer?.msToMark ?? 0,
			};
		}
		setDrafts(seeded);
		setSaveState("idle");
	}, [roundId]);

	// debounced persistence: a reload restores the sheet, without one DB write per slider tick
	useEffect(()=>{
		if (Object.keys(drafts).length == 0) return;
		const timeout = setTimeout(()=>{
			setSaveState("saving");
			const currentRound = latestRound.current;
			SaveAnswers_Live(currentRound.roundId, ToDrafts(currentRound, latestDrafts.current), currentRound.bullets.map(a=>a.slotId))
				.then(()=>setSaveState("saved"))
				.catch(()=>setSaveState("idle"));
		}, 1200);
		return ()=>clearTimeout(timeout);
	}, [drafts, roundId]);

	const setConfidence = (slotId: string, confidence: number)=>{
		setDrafts(prev=>({
			...prev,
			[slotId]: {
				...(prev[slotId] ?? {confidence: DEFAULT_CONFIDENCE, msToMark: 0}),
				confidence,
				// timed on the CHANGE, so it measures when the user made up their mind rather than when
				// the round started
				msToMark: Date.now() - startedAt.current,
			},
		}));
	};
	const setRecallKind = (slotId: string, recallKind: RecallKind|null)=>{
		setDrafts(prev=>({
			...prev,
			[slotId]: {
				...(prev[slotId] ?? {confidence: DEFAULT_CONFIDENCE, msToMark: 0}),
				recallKind,
			},
		}));
	};

	const highlighted = new Set(GetHighlightedSlotIds(round, round.bullets.map(bullet=>({
		slotId: bullet.slotId,
		confidence: drafts[bullet.slotId]?.confidence ?? DEFAULT_CONFIDENCE,
	}))));
	const ratedCount = round.bullets.filter(bullet=>drafts[bullet.slotId] != null).length;

	const submit = async()=>{
		setSubmitting(true);
		try {
			const result = await SubmitRound_Live(roundId, ToDrafts(round, latestDrafts.current));
			if (!result.ok) onError(result.error);
			else onSubmitted(result.reveal, result.suspicion);
		} catch (ex) {
			onError(`${ex}`);
		}
		setSubmitting(false);
	};

	return (
		<Column mt={10} style={{gap: 8}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>{`The round (${round.bullets.length} bullets)`}</Text>
				<Text style={{color: "orange"}}>{`${round.realCount} of them came from your dreams`}</Text>
				<Text style={{opacity: .6}}>{`your guess: ${highlighted.size}/${round.realCount} marked`}</Text>
				{saveState == "saving" && <Text style={{fontSize: 11, opacity: .5}}>saving…</Text>}
				{saveState == "saved" && <Text style={{fontSize: 11, opacity: .5}}>progress saved</Text>}
			</Row>

			<Text style={{fontSize: 12, opacity: .7}}>
				{`0 = definitely written by an LLM · 100 = definitely from one of your dreams. The ${round.realCount} bullets you rate highest are your guess, and are outlined below.`}
			</Text>

			<Column style={{gap: 6}}>
				{round.bullets.map(bullet=>{
					const draft = drafts[bullet.slotId] ?? {confidence: DEFAULT_CONFIDENCE, msToMark: 0};
					return (
						<BulletRatingRow key={bullet.slotId}
							text={bullet.text}
							confidence={draft.confidence}
							recallKind={draft.recallKind ?? null}
							marked={highlighted.has(bullet.slotId)}
							onConfidenceChange={value=>setConfidence(bullet.slotId, value)}
							onRecallKindChange={value=>setRecallKind(bullet.slotId, value)}/>
					);
				})}
			</Column>

			<Row mt={6} center style={{gap: 10}}>
				<Button text={submitting ? "Revealing…" : "Submit and reveal"} enabled={!submitting} onClick={submit}/>
				{submitting && <ProgressSpinner/>}
				<Text style={{opacity: .6}}>{`${ratedCount}/${round.bullets.length} rated`}</Text>
			</Row>

			{round.doorDescription != null &&
				<Column mt={8} style={{gap: 2}}>
					<Text style={{fontWeight: "bold"}}>This round ends at a way out</Text>
					<Text style={{opacity: .85}}>{round.doorDescription}</Text>
					<Text style={{fontSize: 11, opacity: .6}}>You decide what to do about it after the reveal, not during the hunt.</Text>
				</Column>}
		</Column>
	);
});

const BulletRatingRow = observer_mgl((props: {
	text: string,
	confidence: number,
	recallKind: RecallKind|null,
	marked: boolean,
	onConfidenceChange: (value: number)=>void,
	onRecallKindChange: (value: RecallKind|null)=>void,
})=>{
	const {text, confidence, recallKind, marked, onConfidenceChange, onRecallKindChange} = props;

	return (
		<Column style={{
			padding: 8,
			borderRadius: 5,
			background: "rgba(255,255,255,.05)",
			// a thin ring for the current guess: visible, but not a verdict -- every bullet is still
			// judged on its own text
			outline: marked ? "1px solid rgba(255,190,80,.55)" : "1px solid rgba(255,255,255,.07)",
		}}>
			<Text>{text}</Text>
			<Row center mt={5} style={{gap: 8, flexWrap: "wrap"}}>
				<input
					type="range"
					min={0}
					max={100}
					step={5}
					value={confidence}
					style={{flex: 1, minWidth: 120, cursor: "pointer"}}
					onChange={e=>onConfidenceChange(parseInt(e.target.value))}/>
				<Text style={{minWidth: 130, textAlign: "right", fontSize: 12, opacity: .85}}>{DescribeConfidence(confidence)}</Text>
				<Button text="LLM" title="Definitely LLM-sourced (0)" onClick={()=>onConfidenceChange(0)}/>
				<Button text="Dream" title="Definitely dream-sourced (100)" onClick={()=>onConfidenceChange(100)}/>
				{/* all options are objects: an option with no `value` would not compare against the current
				    value under `Select`'s default `compareBy: "value"` */}
				<Select displayType="dropdown" style={{fontSize: 11}}
					options={[RECALL_NONE, ...RecallKind_values.map(a=>({name: RECALL_KIND_LABELS[a], value: a as string}))]}
					value={recallKind ?? RECALL_NONE}
					onChange={(value: string)=>{
						onRecallKindChange(value == RECALL_NONE ? null : value as RecallKind);
					}}/>
			</Row>
		</Column>
	);
});

/** Wording for a confidence value. Shown beside the slider so the number is never the only feedback --
 * "65" means nothing to a first-time player. */
export function DescribeConfidence(confidence: number): string {
	if (confidence >= 90) return `${confidence} -- definitely dream`;
	if (confidence >= 65) return `${confidence} -- probably dream`;
	if (confidence > 35) return `${confidence} -- no idea`;
	if (confidence > 10) return `${confidence} -- probably LLM`;
	return `${confidence} -- definitely LLM`;
}

const RECALL_KIND_LABELS: Record<RecallKind, string> = {
	remember: "remember",
	know: "know",
};

/** The "not reported" option. Kept as its own constant so the comparison against the current value is the
 * same string in both places. */
const RECALL_NONE = "(none)";

// the reveal (§10.1, §10.4)
// ==========

const RevealView = observer_mgl((props: {reveal: HuntRevealView, suspicion: SuspicionApplicationResult|null, onClose: ()=>void})=>{
	const {reveal, suspicion, onClose} = props;
	const {score} = reveal;
	const sourcesBySlot = new Map(reveal.sources.map(source=>[source.slotId, source]));
	/** 1-based position of each bullet in the story, so a tell box can say which bullet it is about. The
	 * reveal lists them in the same order, so the number matches what the player scrolls past. */
	const bulletNumberOf = new Map(reveal.slots.map((slot, index)=>[slot.slotId, index + 1]));
	const bulletTextOf = new Map(reveal.slots.map(slot=>[slot.slotId, SlotText(slot)]));
	/** Offered HERE rather than during play because it shows labels (§16.0); reaching it requires a submitted
	 * round, whose answers are already in. */
	const [showInspector, setShowInspector] = useState(false);
	const round = GetMyEchoRounds().find(a=>a.id == reveal.roundId) ?? null;

	if (showInspector && round != null) {
		return (
			<Column mt={10} style={{gap: 10}}>
				<JudgeInspectorPanel round={round} onClose={()=>setShowInspector(false)}/>
			</Column>
		);
	}

	return (
		<Column sel mt={10} style={{gap: 10}}>
			<Row center style={{gap: 12, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 15}}>Reveal</Text>
				<InfoButton text={[
					"Chance-corrected is raw precision minus the precision random marking would have scored on this",
					"round, divided by the room left above chance (§12). Raw precision alone hands out a free 0.65",
					"when 65% of the story is yours, so it is never shown alone.",
					"",
					"AUC uses every rating, not just your top X: 'what is the chance a random real bullet was rated",
					"above a random fake one'. 0.5 is a coin flip, 1.0 is perfect separation.",
					"",
					"Fakes you spotted get an optional box for the tell that gave them away -- P13 turns those into",
					"cliche-blacklist candidates, which makes later fakes harder.",
				].join("\n")}/>
			</Row>

			<ScoreSummary score={score}/>

			{reveal.judgeSummary != null &&
				<Row center style={{gap: 8, flexWrap: "wrap", padding: 8, borderRadius: 5, background: "rgba(255,255,255,.04)"}}>
					<Text style={{fontWeight: "bold", fontSize: 12}}>Stranger filter</Text>
					<Text style={{fontSize: 12, opacity: .85}}>{DescribeJudgeLoopSummary(reveal.judgeSummary)}</Text>
					<InfoButton text={[
						"Before you saw this round, a model that has never seen your dreams scored every bullet. If it could",
						"not separate the two kinds (AUC near 0.5), then whatever you scored above it is your own familiarity.",
						"",
						"If the loop says it could NOT be made stranger-blind, treat this round's score as an easier test than",
						"usual -- the number is still real, it just had a lower bar.",
						"",
						"The held-out AUC comes from a model the loop never used, so it is the honest estimate.",
					].join("\n")}/>
					{round != null &&
						<Button text="Inspect per-bullet repairs" onClick={()=>setShowInspector(true)}/>}
				</Row>}

			<Column style={{gap: 6}}>
				{reveal.slots.map(slot=>{
					const scored = score.perSlot.find(a=>a.slotId == slot.slotId);
					const source = sourcesBySlot.get(slot.slotId) ?? null;
					return (
						<RevealedBulletRow key={slot.slotId}
							sourceId={slot.sourceId}
							text={SlotText(slot)}
							isReal={slot.sourceKind == "real"}
							marked={scored?.marked ?? false}
							confidence={scored?.confidence ?? DEFAULT_CONFIDENCE}
							rawText={source?.rawText ?? null}
							date={source?.date ?? null}
							dreamId={source?.dreamId ?? null}/>
					);
				})}
			</Column>

			{reveal.tellCandidateSlotIds.length > 0 &&
				<Column mt={6} style={{gap: 6}}>
					<Text style={{fontWeight: "bold"}}>What tipped you off?</Text>
					<Text style={{fontSize: 12, opacity: .75}}>
						Optional, and only for fakes you called. A pattern you can name is a pattern we can keep out of the
						next batch.
					</Text>
					{reveal.tellCandidateSlotIds.map(slotId=>
						<TellBox key={slotId} roundId={reveal.roundId} slotId={slotId}
							bulletNumber={bulletNumberOf.get(slotId) ?? 0}
							bulletText={bulletTextOf.get(slotId) ?? null}
							initialPhrase={reveal.tellPhrases[slotId] ?? ""}/>)}
				</Column>}

			{reveal.doorDescription != null && round?.storyId != null &&
				<DoorVerdictBox roundId={reveal.roundId} storyId={round.storyId} description={reveal.doorDescription}
					targetWorldName={TargetWorldNameForRound(round)}/>}

			{suspicion != null && suspicion.after != suspicion.before &&
				<Text style={{fontSize: 12, opacity: .8}}>
					{`Suspicion ${Math.round(suspicion.before * 100)}% → ${Math.round(suspicion.after * 100)}%`}
					{suspicion.applied ? "" : " (recorded only -- applying suspicion is off)"}
				</Text>}

			{suspicion != null && suspicion.ended && suspicion.interlude != null &&
				<Column mt={6} style={{gap: 4, padding: 10, borderRadius: 5, background: "rgba(255,190,80,.10)"}}>
					<Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontWeight: "bold"}}>The character noticed</Text>
						<InfoButton text={[
							"§8.4: your correct echo finds raised the character's suspicion until they noticed something was wrong,",
							"checked, and woke up. This ends the story -- the next one follows a different character from your pool.",
							"",
							"The sequence is the one you are practising in the hunt: 'this is oddly familiar', check, realise.",
						].join("\n")}/>
						{suspicion.interludeIsFallback &&
							<Text style={{fontSize: 11, color: "orange"}}>
								{suspicion.interludeError != null
									? `Written by the fallback narrator (${suspicion.interludeError})`
									: "Written by the fallback narrator"}
							</Text>}
					</Row>
					<Text style={{opacity: .9}}>{suspicion.interlude}</Text>
					<Text style={{fontSize: 11, opacity: .6}}>
						The whole run is on the Story tab, including the world you ended up in and the rounds you played.
					</Text>
				</Column>}

			<Row mt={10}>
				<Button text="Done" onClick={onClose}/>
			</Row>
		</Column>
	);
});

const ScoreSummary = observer_mgl((props: {score: HuntScore})=>{
	const {score} = props;

	return (
		<Column style={{padding: 10, borderRadius: 5, background: "rgba(255,255,255,.06)", gap: 4}}>
			<Row center style={{gap: 18, flexWrap: "wrap"}}>
				<Column>
					<Text style={{fontSize: 11, opacity: .6}}>Chance-corrected</Text>
					<Text style={{fontSize: 20, fontWeight: "bold", color: score.chanceCorrected > 0 ? "lightgreen" : "orange"}}>
						{FormatSigned(score.chanceCorrected)}
					</Text>
				</Column>
				<Column>
					<Text style={{fontSize: 11, opacity: .6}}>Precision</Text>
					<Text style={{fontSize: 20}}>{FormatPercent(score.precision)}</Text>
				</Column>
				<Column>
					<Text style={{fontSize: 11, opacity: .6}}>Chance</Text>
					<Text style={{fontSize: 20, opacity: .7}}>{FormatPercent(score.chancePrecision)}</Text>
				</Column>
				<Column>
					<Text style={{fontSize: 11, opacity: .6}}>Recall</Text>
					<Text style={{fontSize: 20}}>{FormatPercent(score.recall)}</Text>
				</Column>
				<Column>
					<Text style={{fontSize: 11, opacity: .6}}>AUC</Text>
					<Text style={{fontSize: 20}}>{score.auc.toFixed(2)}</Text>
				</Column>
			</Row>
			<Text style={{fontSize: 12, opacity: .8}}>
				{`${score.hits} of ${score.realCount} echoes found · ${score.falseAlarms} false alarm(s) · ${score.misses} missed`}
			</Text>
		</Column>
	);
});

const RevealedBulletRow = observer_mgl((props: {
	/** the corpus row id for a real slot -- what the exposure counter is keyed on */
	sourceId: string,
	text: string,
	isReal: boolean,
	marked: boolean,
	confidence: number,
	rawText: string|null,
	date: number|null,
	dreamId: string|null,
})=>{
	const {sourceId, text, isReal, marked, confidence, rawText, date, dreamId} = props;
	const [opened, setOpened] = useState(false);

	const correct = marked == isReal;
	const accent = marked
		? (isReal ? "lightgreen" : "tomato")
		: (isReal ? "orange" : "rgba(255,255,255,.35)");

	const openFullDream = ()=>{
		if (dreamId == null) return;
		const entryId = dreamId.split("/")[0];
		RunInAction_Set(()=>{
			store.main.page = "journal";
			store.main.journal.selectedJournalEntry = entryId;
		});
		// opening the full dream is a STRONGER exposure than seeing the raw bullet (§10.4)
		RecordRevealOpen_Live(sourceId).catch(()=>{ /* exposure counters feed a weight, not a score */ });
	};

	return (
		<Column style={{padding: 8, borderRadius: 5, background: "rgba(255,255,255,.05)", borderLeft: `3px solid ${accent}`}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, color: accent, fontWeight: "bold"}}>{isReal ? "dream" : "LLM"}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{`you: ${confidence}`}</Text>
				<Text style={{fontSize: 11, color: correct ? "lightgreen" : "tomato"}}>
					{marked
						? (isReal ? "marked, and it was yours" : "marked, but it was a fake")
						: (isReal ? "you let this echo go" : "correctly left alone")}
				</Text>
			</Row>
			<Text style={{marginTop: 3}}>{text}</Text>

			{isReal && rawText != null &&
				<Column mt={4} style={{gap: 2}}>
					<Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 11, opacity: .6}}>
							{`original${date != null ? ` · ${new Date(date).toLocaleDateString()}` : ""}`}
						</Text>
						{dreamId != null &&
							<Button text={opened ? "Opened" : "Open full dream"} enabled={!opened} onClick={()=>{
								setOpened(true);
								openFullDream();
							}}/>}
					</Row>
					<Text style={{fontSize: 12, opacity: .8}}>{rawText}</Text>
				</Column>}
		</Column>
	);
});

const TellBox = observer_mgl((props: {roundId: string, slotId: string, bulletNumber?: number, bulletText?: string|null, initialPhrase?: string|null})=>{
	const {roundId, slotId, bulletNumber, bulletText, initialPhrase} = props;
	// seeded from the STORED phrase, so re-opening a finished round shows the note that was already saved
	// rather than an empty box (the phrase lives on the round's answer -- nothing else to load)
	const [phrase, setPhrase] = useState(initialPhrase ?? "");
	/** the phrase this box has already persisted; "" = nothing stored. Compared against the draft to decide
	 * whether there is anything to save, which is what lets an emptied box mean "delete". */
	const [storedPhrase, setStoredPhrase] = useState(initialPhrase ?? "");

	/** an empty draft means DELETE, so it is only a no-op when there was nothing stored to begin with */
	const draft = phrase.trim();
	const willDelete = draft.length == 0 && storedPhrase.length > 0;
	const dirty = draft != storedPhrase;
	const canSave = dirty;

	return (
		<Row center style={{gap: 6, flexWrap: "wrap"}}>
			<InfoButton text={[
				bulletNumber ? `Bullet ${bulletNumber} of the story -- the one this note is about.` : "The story bullet this note is about.",
				"",
				bulletText ?? "(that bullet's text is not available)",
			].join("\n")}/>
			<TextArea autoSize={true} instant style={{flex: 1, fontSize: 12}}
				title="e.g. 'neatly resolved', 'someone explained the rules'"
				value={phrase}
				onChange={setPhrase}/>
			<Button
				text={willDelete ? "Delete" : "Save"}
				enabled={canSave}
				onClick={async()=>{
					await SaveTellPhrase_Live(roundId, slotId, willDelete ? null : draft);
					setStoredPhrase(willDelete ? "" : draft);
				}}/>
			{storedPhrase.length > 0 && !dirty &&
				<Text style={{fontSize: 11, opacity: .5}}>saved</Text>}
		</Row>
	);
});

// the door (§8.3, P11)
// ==========

/** The name of the world a round's door leads to, for the verdict box. Read from the offer's fixed target,
 * falling back to nothing rather than guessing -- a pre-11.5 offer has no target, and inventing one would
 * promise a destination that `ResolveDoorDestination` might not honour. */
function TargetWorldNameForRound(round: {doorOffer?: {targetWorldId?: string|n}|n}|n): string|null {
	const targetId = round?.doorOffer?.targetWorldId ?? null;
	if (targetId == null) return null;
	return GetWorldPool_Live().find(a=>a.id == targetId)?.name ?? null;
}

/** Warns when the round in front of the player was written somewhere the story has since left (§11.5 W7).
 * Not a block -- the round is coherent in its own world and the player paid for it -- but without this the
 * tab would contradict `ActiveStoryLine`. P13's lookahead needs the same signal. */
const StaleRoundNotice = observer_mgl((props: {roundId: string})=>{
	const {roundId} = props;
	const story = GetActiveStory_Live();
	const round = GetMyEchoRounds().find(a=>a.id == roundId) ?? null;
	const [discarding, setDiscarding] = useState(false);

	if (story == null || round == null) return <></>;
	if (!IsRoundStaleAgainstStory(round, story, GetStoryWorldId_Live(story))) return <></>;

	const roundWorldName = GetWorldPool_Live().find(a=>a.id == round.worldId)?.name ?? "(a world you have since removed)";

	return (
		<Row mt={8} center style={{gap: 10, flexWrap: "wrap", padding: 8, borderRadius: 5, background: "rgba(255,170,60,.10)"}}>
			<Text style={{fontSize: 12, color: "orange"}}>
				{`This round was written in ${roundWorldName}, but the story has since moved on. It still reads as its own stretch -- or discard it and build one where the story is now.`}
			</Text>
			<Button text={discarding ? "Discarding…" : "Discard it"} enabled={!discarding} onClick={async()=>{
				setDiscarding(true);
				// revealing it is what removes it from the playable set; a discard marker keeps the round in
				// the log rather than deleting a row the player paid for
				await MarkRoundDiscarded_Live(round.id).catch(()=>{ /* the notice simply stays */ });
				setDiscarding(false);
			}}/>
		</Row>
	);
});

/** The PLAYER's verdict on a door the weaver offered (§11.5 W2) -- an AUTHORIAL question, not an in-fiction
 * one: do you want this possibility in the story? Accepting stages the door for the NEXT round, where the
 * character decides whether to use it. */
const DoorVerdictBox = observer_mgl((props: {roundId: string, storyId: string, description: string, targetWorldName: string|null})=>{
	const {roundId, storyId, description, targetWorldName} = props;
	const story = GetMyEchoStories_Live().find(a=>a.id == storyId) ?? null;
	const round = GetMyEchoRounds().find(a=>a.id == roundId) ?? null;
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string|null>(null);

	const verdict = round?.doorVerdict ?? null;

	const choose = async(next: DoorVerdict)=>{
		setBusy(true);
		setError(null);
		try {
			const result = await SetDoorVerdict_Live({storyId, roundId, verdict: next});
			if (!result.ok) setError(result.error);
		} catch (ex) {
			setError(`${ex}`);
		}
		setBusy(false);
	};

	// the story may have moved on since this round was revealed: say why, rather than showing dead buttons
	const gate = round != null ? CanAnswerDoor(story, round, GetStoryRounds_Live(storyId)) : {ok: false as const, reason: "noOffer" as const};
	const blockedReason = verdict == null && !gate.ok ? gate.reason : null;

	return (
		<Column mt={6} style={{gap: 5, padding: 10, borderRadius: 5, background: "rgba(120,200,255,.08)"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>A way out</Text>
				<InfoButton text={[
					"§11.5: a door is offered to YOU first, and this is the one place the story's direction is yours",
					"rather than the character's. The character has not seen it yet.",
					"",
					"Accept, and it appears in the next round as something they can see and could use -- and THEY decide",
					"whether to walk through it. The story only moves if they do, which is what keeps their choices theirs.",
					"",
					"Decline, and nothing about the story changes. A later round can offer another.",
				].join("\n")}/>
				{targetWorldName != null &&
					<Text style={{fontSize: 11, opacity: .6}}>{`leads to ${targetWorldName}`}</Text>}
			</Row>
			<Text style={{opacity: .9}}>{description}</Text>

			{verdict == null && blockedReason == null &&
				<Row center style={{gap: 10, flexWrap: "wrap"}}>
					<Button text={busy ? "Saving…" : "Accept this door"} enabled={!busy} onClick={()=>choose("accept")}/>
					<Button text="Not this one" enabled={!busy} onClick={()=>choose("decline")}/>
					{busy && <ProgressSpinner/>}
				</Row>}

			{verdict != null &&
				<Text style={{fontSize: 12, opacity: .75}}>
					{verdict == "accept"
						? `Accepted. It will be waiting for the character in the next round${round?.doorDecision == "taken" ? ", and they went through it." : "."}`
						: "Declined. The story stays where it is."}
				</Text>}
			{verdict == "accept" && round?.doorDecision == null &&
				<Text style={{fontSize: 11, opacity: .6}}>Whether they use it is decided in the next round.</Text>}

			{blockedReason != null &&
				<Text style={{fontSize: 11, opacity: .6}}>{DescribeDoorBlock(blockedReason)}</Text>}

			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

// recent reveals (a way back into a finished round)
// ==========

/** The finished rounds of the story this tab is building into -- NOT every round on the account. A round
 * from another story is not this tab's history, and re-opening it offers its door, which would direct a run
 * the player is no longer in. */
const RecentReveals = observer_mgl((props: {onOpen: (reveal: HuntRevealView)=>void})=>{
	const rounds = GetPlayableStoryRevealedRounds_Live();
	const story = GetActiveStory_Live();
	const character = GetStoryCharacter_Live(story);

	if (rounds.length == 0) return <></>;

	return (
		<Column mt={16} style={{gap: 4}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", opacity: .85}}>
					{story != null
						? `Finished rounds of this story (${character?.name ?? "(no character)"})`
						: "Finished rounds (no story)"}
				</Text>
				<InfoButton text={[
					"Only rounds belonging to the story the Play tab is building into are listed here.",
					"",
					"Switch stories on the Story tab to see another run's rounds -- each story keeps its own history.",
				].join("\n")}/>
			</Row>
			{rounds.slice(0, 8).map(round=>{
				return (
					<Row key={round.id} style={{gap: 8}}>
						<Button text={new Date(round.createdAt).toLocaleString()} onClick={async()=>{
							const storedReveal = await GetAsync(()=>GetRevealView_Live(round));
							if (storedReveal != null) props.onOpen(storedReveal);
						}}/>
						<Text style={{fontSize: 11, opacity: .55}}>{`${(round.slots ?? []).length} bullets`}</Text>
					</Row>
				);
			})}
		</Column>
	);
});

// helpers
// ==========

/** Builds the persisted draft list from the live sheet, in story order. */
function ToDrafts(round: HuntRoundView, drafts: DraftSheet) {
	return round.bullets.map(bullet=>{
		const draft = drafts[bullet.slotId];
		return {
			slotId: bullet.slotId,
			confidence: draft?.confidence ?? DEFAULT_CONFIDENCE,
			recallKind: draft?.recallKind ?? null,
			why: draft?.why ?? null,
			msToMark: draft?.msToMark ?? 0,
		};
	});
}

function FormatPercent(value: number): string {
	return `${Math.round(value * 100)}%`;
}

function FormatSigned(value: number): string {
	const percent = Math.round(value * 100);
	return `${percent > 0 ? "+" : ""}${percent}%`;
}
